<template>
  <div class="admin-dashboard">
    <v-container fluid class="pa-6">
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <StatCard
            icon="mdi-domain"
            title="สถานศึกษาทั้งหมด"
            :value="statistics.totalColleges"
            color="primary"
            unit="แห่ง"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <StatCard
            icon="mdi-school-check"
            title="รายงานแล้ว"
            :value="statistics.reportedColleges"
            color="success"
            unit="แห่ง"
            :percentage="reportPercentage"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <StatCard
            icon="mdi-account-clock"
            title="รออนุมัติทั้งหมด"
            :value="statistics.pendingApproval"
            color="warning"
            unit="รายการ"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <StatCard
            icon="mdi-account-tie-voice"
            title="ผู้ทรงคุณวุฒิทั้งหมด"
            :value="statistics.totalQualified"
            color="info"
            unit="คน"
          />
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12" md="6">
          <v-card elevation="4" class="chart-card">
            <v-card-title class="teal white--text py-3">
              <v-icon left>mdi-account-group</v-icon>
              สัดส่วนผู้ทรงคุณวุฒิ
            </v-card-title>
            <v-card-text class="pa-4">
              <apexchart
                type="donut"
                :options="donutChartOptions"
                :series="donutChartSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card elevation="4" class="chart-card">
            <v-card-title class="teal white--text py-3">
              <v-icon left>mdi-chart-bar</v-icon>
              สถานะการรายงาน (ความคืบหน้า)
            </v-card-title>
            <v-card-text class="pa-4">
              <apexchart
                type="radialBar"
                :options="radialChartOptions"
                :series="radialChartSeries"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-6">
        <v-col cols="12">
          <v-card elevation="4">
            <v-card-title class="primary white--text py-3">
              <v-icon left>mdi-format-list-bulleted</v-icon>
              รายการผู้ทรงคุณวุฒิทั้งหมด
            </v-card-title>

            <v-card-text class="py-4">
              <v-row>
                <v-col cols="12" sm="4" md="3">
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="ค้นหา (ชื่อ, สถานศึกษา)"
                    outlined
                    dense
                    clearable
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <v-select
                    v-model="filterType"
                    :items="filterTypeOptions"
                    label="กรองตามประเภท"
                    outlined
                    dense
                    hide-details
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <v-select
                    v-model="filterStatus"
                    :items="filterStatusOptions"
                    label="กรองตามสถานะ"
                    outlined
                    dense
                    hide-details
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3" class="d-flex justify-end">
                  <v-btn color="primary" @click="exportToExcel('filtered')">
                    <v-icon left>mdi-file-excel</v-icon>
                    ส่งออกข้อมูลที่กรอง
                  </v-btn>
                  <v-btn
                    icon
                    color="teal"
                    class="ml-2"
                    @click="refreshData"
                    title="รีเฟรชข้อมูล"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>

            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="filteredData"
              :search="search"
              :items-per-page="15"
              class="elevation-0"
              show-select
              v-model="selectedItems"
            >
              <template v-slot:item.assistantcommitteeQualifiedType="{ item }">
                <v-chip
                  :color="
                    item.assistantcommitteeQualifiedType === 'inside'
                      ? 'blue-grey'
                      : 'orange'
                  "
                  small
                  text-color="white"
                >
                  {{
                    item.assistantcommitteeQualifiedType === "inside"
                      ? "ภายใน สอศ."
                      : "ภายนอก"
                  }}
                </v-chip>
              </template>

              <template v-slot:item.approvalStatus="{ item }">
                <v-chip
                  small
                  label
                  :color="getApprovalStatusColor(item.approvalStatus)"
                  text-color="white"
                >
                  <v-icon left small>{{
                    getApprovalStatusIcon(item.approvalStatus)
                  }}</v-icon>
                  {{ getApprovalStatusText(item.approvalStatus) }}
                </v-chip>
              </template>

              <template v-slot:item.assistantcommitteeQualifiedFile="{ item }">
                <v-btn
                  icon
                  small
                  color="purple"
                  @click="viewPdfFile(item.assistantcommitteeQualifiedFile)"
                  v-if="item.assistantcommitteeQualifiedFile"
                  title="ดูเอกสาร"
                >
                  <v-icon small>mdi-file-pdf-box</v-icon>
                </v-btn>
                <span v-else>-</span>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  small
                  color="info"
                  class="mr-1"
                  @click="viewDetail(item)"
                  title="ดูรายละเอียด"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="success"
                  class="mr-1"
                  @click="approveItem(item)"
                  :disabled="item.approvalStatus === 'approved'"
                  title="อนุมัติ"
                >
                  <v-icon small>mdi-check</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="error"
                  @click="rejectItem(item)"
                  :disabled="item.approvalStatus === 'rejected'"
                  title="ไม่อนุมัติ"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </template>

              <template v-slot:footer>
                <v-card-actions class="px-4">
                  <span class="grey--text text-caption"
                    >เลือกไว้ {{ selectedItems.length }} รายการ</span
                  >
                  <v-btn
                    small
                    color="success"
                    class="ml-3"
                    :disabled="selectedItems.length === 0"
                    @click="bulkApprove"
                  >
                    <v-icon left small>mdi-check-all</v-icon>
                    อนุมัติทั้งหมด
                  </v-btn>
                  <v-btn
                    small
                    color="error"
                    class="ml-2"
                    :disabled="selectedItems.length === 0"
                    @click="bulkReject"
                  >
                    <v-icon left small>mdi-close-octagon-outline</v-icon>
                    ไม่อนุมัติทั้งหมด
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-data-table-footer></v-data-table-footer>
                </v-card-actions>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="detailDialog" max-width="700px">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-account-details</v-icon>
          รายละเอียดผู้ทรงคุณวุฒิ
          <v-spacer></v-spacer>
          <v-btn icon dark @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6" v-if="selectedDetail">
          <v-row>
            <v-col cols="12" md="6">
              <div class="detail-section">
                <h4 class="text-subtitle-2 grey--text mb-2">ข้อมูลส่วนตัว</h4>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon
                      ><v-icon small
                        >mdi-card-account-details</v-icon
                      ></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-subtitle
                        >รหัสบัตรประชาชน</v-list-item-subtitle
                      >
                      <v-list-item-title class="font-weight-medium">{{
                        selectedDetail.assistantcommitteeQualifiedIDcard
                      }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon
                      ><v-icon small>mdi-account</v-icon></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-subtitle>ชื่อ-นามสกุล</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-medium">
                        {{ selectedDetail.assistantcommitteeQualifiedTitles
                        }}{{
                          selectedDetail.assistantcommitteeQualifiedFristname
                        }}
                        {{ selectedDetail.assistantcommitteeQualifiedLastname }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon
                      ><v-icon small>mdi-briefcase</v-icon></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-subtitle>ตำแหน่ง</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-medium">{{
                        selectedDetail.assistantcommitteeQualifiedPosition ||
                          "-"
                      }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon
                      ><v-icon small>mdi-numeric</v-icon></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-subtitle>อันดับ</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-medium">
                        <v-chip
                          :color="
                            getRankColor(
                              selectedDetail.assistantcommitteeQualifiedRank
                            )
                          "
                          small
                          dark
                          label
                        >
                          {{
                            selectedDetail.assistantcommitteeQualifiedRank ||
                              "-"
                          }}
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="detail-section">
                <h4 class="text-subtitle-2 grey--text mb-2">ข้อมูลการศึกษา</h4>
                <v-list dense>
                  <v-list-item
                    v-if="selectedDetail.assistantcommitteeQualifiedEDDoctoral"
                  >
                    <v-list-item-icon
                      ><v-icon small>mdi-school</v-icon></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-subtitle>ปริญญาเอก</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-medium">
                        {{
                          selectedDetail.assistantcommitteeQualifiedEDDoctoralSub
                        }}
                        {{
                          selectedDetail.assistantcommitteeQualifiedEDDoctoral
                        }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-if="selectedDetail.assistantcommitteeQualifiedEDMaster"
                  >
                    <v-list-item-icon
                      ><v-icon small>mdi-school</v-icon></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-subtitle>ปริญญาโท</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-medium">
                        {{
                          selectedDetail.assistantcommitteeQualifiedEDMasterSub
                        }}
                        {{ selectedDetail.assistantcommitteeQualifiedEDMaster }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-if="selectedDetail.assistantcommitteeQualifiedED"
                  >
                    <v-list-item-icon
                      ><v-icon small>mdi-school</v-icon></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-subtitle>ปริญญาตรี</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-medium">
                        {{ selectedDetail.assistantcommitteeQualifiedEDSub }}
                        {{ selectedDetail.assistantcommitteeQualifiedED }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-col>
            <v-col cols="12">
              <div class="detail-section">
                <h4 class="text-subtitle-2 grey--text mb-2">ข้อมูลสถานศึกษา</h4>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon
                      ><v-icon small>mdi-domain</v-icon></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-subtitle>สถานศึกษา</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-medium">{{
                        selectedDetail.college_name
                      }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon
                      ><v-icon small
                        >mdi-check-decagram</v-icon
                      ></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-subtitle>สถานะปัจจุบัน</v-list-item-subtitle>
                      <v-list-item-title>
                        <v-chip
                          :color="
                            getApprovalStatusColor(
                              selectedDetail.approvalStatus
                            )
                          "
                          small
                          dark
                          label
                        >
                          {{
                            getApprovalStatusText(selectedDetail.approvalStatus)
                          }}
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="detailDialog = false">ปิด</v-btn>
          <v-btn
            color="success"
            @click="approveItem(selectedDetail)"
            :disabled="
              selectedDetail && selectedDetail.approvalStatus === 'approved'
            "
          >
            <v-icon left>mdi-check</v-icon>
            อนุมัติ
          </v-btn>
          <v-btn
            color="error"
            @click="rejectItem(selectedDetail)"
            :disabled="
              selectedDetail && selectedDetail.approvalStatus === 'rejected'
            "
          >
            <v-icon left>mdi-close</v-icon>
            ไม่อนุมัติ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="pdfDialog"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="pdfDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>เอกสารยืนยันตัวตน</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              icon
              dark
              :href="'/HRCommitteeQf/' + pdfFile"
              target="_blank"
              title="เปิดในแท็บใหม่"
            >
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="pa-0 fill-height">
          <object
            :data="'/HRCommitteeQf/' + pdfFile"
            type="application/pdf"
            width="100%"
            height="calc(100vh - 64px)"
            style="min-height: 500px;"
          >
            <p>
              ไม่สามารถแสดงไฟล์ PDF ได้ กรุณา
              <a :href="'/HRCommitteeQf/' + pdfFile" target="_blank"
                >ดาวน์โหลดไฟล์</a
              >
              แทน
            </p>
          </object>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="approvalDialog" max-width="500px">
      <v-card>
        <v-card-title class="success white--text">
          <v-icon left color="white">mdi-check-circle</v-icon>
          ยืนยันการอนุมัติ
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="font-weight-medium">
            ต้องการอนุมัติผู้ทรงคุณวุฒิรายการนี้หรือไม่?
          </p>
          <v-alert type="info" dense text v-if="approvalTarget" class="mb-3">
            {{ approvalTarget.assistantcommitteeQualifiedTitles
            }}{{ approvalTarget.assistantcommitteeQualifiedFristname }}
            {{ approvalTarget.assistantcommitteeQualifiedLastname }}
          </v-alert>
          <v-textarea
            v-model="approvalNote"
            label="หมายเหตุ (ถ้ามี)"
            rows="2"
            outlined
            dense
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="approvalDialog = false">ยกเลิก</v-btn>
          <v-btn color="success" depressed @click="confirmApproval">
            <v-icon left>mdi-check</v-icon>
            ยืนยันอนุมัติ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="500px">
      <v-card>
        <v-card-title class="error white--text">
          <v-icon left color="white">mdi-close-circle</v-icon>
          ไม่อนุมัติ
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="font-weight-medium">
            ต้องการไม่อนุมัติผู้ทรงคุณวุฒิรายการนี้หรือไม่?
          </p>
          <v-alert type="warning" dense text v-if="rejectTarget" class="mb-3">
            {{ rejectTarget.assistantcommitteeQualifiedTitles
            }}{{ rejectTarget.assistantcommitteeQualifiedFristname }}
            {{ rejectTarget.assistantcommitteeQualifiedLastname }}
          </v-alert>
          <v-textarea
            v-model="rejectReason"
            label="เหตุผลที่ไม่อนุมัติ *"
            rows="3"
            outlined
            dense
            :rules="[v => !!v || 'กรุณาระบุเหตุผล']"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="rejectDialog = false">ยกเลิก</v-btn>
          <v-btn
            color="error"
            depressed
            @click="confirmReject"
            :disabled="!rejectReason"
          >
            <v-icon left>mdi-close</v-icon>
            ยืนยันไม่อนุมัติ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
    >
      <v-icon left>{{ snackbar.icon }}</v-icon>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import * as XLSX from "xlsx";
// สร้าง Component ย่อยสำหรับ StatCard เพื่อให้โค้ดสะอาดขึ้น
const StatCard = {
  props: ["icon", "title", "value", "color", "unit", "percentage"],
  template: `
    <v-card :elevation="4" :class="['stat-card', 'stat-card--' + color]" dark>
      <v-card-text class="pa-4 d-flex align-center justify-space-between">
        <div>
          <div class="text-subtitle-2 font-weight-light">{{ title }}</div>
          <div class="text-h4 font-weight-bold mt-1">
            {{ value }} <span class="text-subtitle-1 font-weight-regular">{{ unit }}</span>
          </div>
          <v-chip v-if="percentage" small outlined class="mt-1" color="white">
            <v-icon left small>mdi-trending-up</v-icon> {{ percentage }}%
          </v-chip>
        </div>
        <v-icon size="48" class="opacity-3">{{ icon }}</v-icon>
      </v-card-text>
    </v-card>
  `
};

export default {
  name: "AdminAssistantCommitteeQualified",
  components: {
    apexchart: VueApexCharts,
    StatCard // Register the new component
  },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: true,
      search: "",

      // Filters
      filterType: "all",
      filterStatus: "all",
      filterTypeOptions: [
        { text: "ทั้งหมด", value: "all" },
        { text: "ภายใน สอศ.", value: "inside" },
        { text: "ภายนอก/เกษียณ", value: "outside" }
      ],
      filterStatusOptions: [
        { text: "ทั้งหมด", value: "all" },
        { text: "รออนุมัติ", value: "pending" },
        { text: "อนุมัติแล้ว", value: "approved" },
        { text: "ไม่อนุมัติ", value: "rejected" }
      ],

      // Data
      assistantcommitteequalifieds: [], // Main data list
      selectedItems: [], // For bulk actions
      periodTeachersAssisstant: {},
      colleges: [], // List of all colleges
      assistantcommitteequalified_proposals: [], // College report status data (renamed for clarity)

      // Statistics
      statistics: {
        totalColleges: 0,
        reportedColleges: 0,
        pendingApproval: 0,
        totalQualified: 0,
        insideCount: 0,
        outsideCount: 0
      },

      // Dialogs
      detailDialog: false,
      pdfDialog: false,
      approvalDialog: false,
      rejectDialog: false,
      selectedDetail: null,
      pdfFile: "",
      approvalTarget: null,
      approvalNote: "",
      rejectTarget: null,
      rejectReason: "",

      // Snackbar
      snackbar: {
        show: false,
        color: "",
        timeout: 4000,
        icon: "",
        text: ""
      },

      // Table Headers - Main Qualified List
      headers: [
        {
          text: "ประเภท",
          value: "assistantcommitteeQualifiedType",
          align: "center",
          width: "130"
        },
        { text: "สถานศึกษา", value: "college_name", width: "250" },
        { text: "ชื่อ-นามสกุล", value: "fullname", width: "200" },
        {
          text: "ตำแหน่ง",
          value: "assistantcommitteeQualifiedPosition",
          width: "180"
        },
        {
          text: "อันดับ",
          value: "assistantcommitteeQualifiedRank",
          align: "center",
          width: "100"
        },
        {
          text: "สถานะ",
          value: "approvalStatus",
          align: "center",
          width: "120"
        },
        {
          text: "เอกสาร",
          value: "assistantcommitteeQualifiedFile",
          align: "center",
          sortable: false,
          width: "80"
        },
        {
          text: "จัดการ",
          value: "actions",
          align: "center",
          sortable: false,
          width: "140"
        }
      ],

      // Table Headers - College Report Status
      collegeHeaders: [
        { text: "รหัสสถานศึกษา", value: "college_code", width: "120" },
        { text: "ชื่อสถานศึกษา", value: "college_name" },
        { text: "สถานะ", value: "reportStatus", align: "center", width: "130" },
        {
          text: "จำนวนผู้ทรง",
          value: "qualifiedCount",
          align: "center",
          width: "120"
        },
        { text: "อัพเดตล่าสุด", value: "updated_at", width: "150" },
        { text: "File", value: "actions", sortable: false, width: "60" }
      ],

      // Chart Options
      donutChartOptions: {
        chart: {
          type: "donut",
          fontFamily: "Sarabun, sans-serif"
        },
        colors: ["#26A69A", "#FFC107"], // Updated colors for better visual separation
        labels: ["ภายใน สอศ.", "ภายนอก/เกษียณ"],
        legend: {
          position: "bottom",
          fontSize: "14px"
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "รวมทั้งหมด",
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#333"
                },
                value: {
                  fontSize: "16px"
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val, opts) => {
            const name = opts.w.config.labels[opts.seriesIndex];
            return [name, val.toFixed(1) + "%"];
          },
          style: {
            fontSize: "12px"
          }
        }
      },
      donutChartSeries: [65, 35], // Initial dummy data

      radialChartOptions: {
        chart: {
          type: "radialBar",
          fontFamily: "Sarabun, sans-serif"
        },
        colors: ["#4CAF50", "#FFC107", "#F44336"],
        plotOptions: {
          radialBar: {
            hollow: {
              margin: 5,
              size: "50%"
            },
            dataLabels: {
              name: { fontSize: "16px" },
              value: {
                fontSize: "24px",
                fontWeight: 700,
                formatter: val => val + "%"
              },
              total: {
                show: true,
                label: "ความคืบหน้า",
                formatter: () => `${this.reportPercentage}%` // Dynamically set total
              }
            },
            track: { background: "#f0f0f0" }
          }
        },
        labels: ["รายงานแล้ว", "รออนุมัติ", "ยังไม่รายงาน"]
      },
      radialChartSeries: [75, 15, 10] // Initial dummy data
    };
  },

  computed: {
    reportPercentage() {
      if (this.statistics.totalColleges === 0) return 0;
      return (
        (this.statistics.reportedColleges / this.statistics.totalColleges) *
        100
      ).toFixed(1);
    },

    filteredData() {
      let data = this.assistantcommitteequalifieds;

      // 1. Filter by Type
      if (this.filterType !== "all") {
        data = data.filter(
          item => item.assistantcommitteeQualifiedType === this.filterType
        );
      }

      // 2. Filter by Status
      if (this.filterStatus !== "all") {
        data = data.filter(
          item => (item.approvalStatus || "pending") === this.filterStatus
        );
      }

      // 3. Search is handled by v-data-table (if you use :search prop)
      // Since v-data-table handles search, we only need to return the filtered list by select boxes.
      // If we need to filter by search manually, we can uncomment below:
      /*
      if (this.search) {
          const lowerSearch = this.search.toLowerCase();
          data = data.filter(item => 
              (item.college_name && item.college_name.toLowerCase().includes(lowerSearch)) ||
              (item.fullname && item.fullname.toLowerCase().includes(lowerSearch))
          );
      }
      */

      return data;
    }
  },

  async mounted() {
    await this.initializeData();
  },

  methods: {
    // --- Data Initialization and Fetching ---
    async initializeData() {
      this.loading = true;
      try {
        await Promise.all([
          this.periodQueryTeacherAssisstant(),
          this.collegeQueryAll()
        ]);
        await this.assistantcommitteequalifiedsQueryAll();
        await this.assistantcommitteequalified_proposalsQueryAll();

        this.calculateStatistics();
        this.updateCharts();
      } catch (error) {
        console.error("Error initializing data:", error);
        this.showSnackbar(
          "error",
          "mdi-alert",
          "เกิดข้อผิดพลาดในการโหลดข้อมูล"
        );
      } finally {
        this.loading = false;
      }
    },

    async refreshData() {
      await this.initializeData();
      this.showSnackbar("success", "mdi-check", "รีเฟรชข้อมูลเรียบร้อย");
    },

    async assistantcommitteequalified_proposalsQueryAll() {
      // API call to get college report status data
      let result = await this.$http.post(
        "assistantcommitteequalified_proposal.php",
        {
          ApiKey: this.ApiKey,
          action: "get_proposal_admin",
          period_year: this.periodTeachersAssisstant.period_college_year // Assuming this year is needed
        }
      );

      this.assistantcommitteequalified_proposals = result.data || [];
    },

    async collegeQueryAll() {
      // API call to get all college list
      const result = await this.$http.post("college.php", {
        ApiKey: this.ApiKey
      });
      this.colleges = result.data || [];
    },

    async periodQueryTeacherAssisstant() {
      // API call to get current period year
      const result = await this.$http.post("period_college.php", {
        ApiKey: this.ApiKey,
        period_college_id: "60101"
      });
      this.periodTeachersAssisstant = result.data;
    },

    async assistantcommitteequalifiedsQueryAll() {
      // API call to get main qualified list
      const result = await this.$http.post("assistantcommitteequalified.php", {
        ApiKey: this.ApiKey,
        assistantcommitteeQualifiedAdmin: "ok"
      });

      // Map data to include 'fullname' for easier display/search
      this.assistantcommitteequalifieds = (result.data || []).map(item => ({
        ...item,
        fullname: `${item.assistantcommitteeQualifiedTitles ||
          ""}${item.assistantcommitteeQualifiedFristname ||
          ""} ${item.assistantcommitteeQualifiedLastname || ""}`
      }));
    },

    // --- Statistics and Chart Logic ---
    calculateStatistics() {
      const data = this.assistantcommitteequalifieds;

      this.statistics.totalQualified = data.length;
      this.statistics.insideCount = data.filter(
        d => d.assistantcommitteeQualifiedType === "inside"
      ).length;
      this.statistics.outsideCount = data.filter(
        d => d.assistantcommitteeQualifiedType === "outside"
      ).length;
      this.statistics.pendingApproval = data.filter(
        d => !d.approvalStatus || d.approvalStatus === "pending"
      ).length;

      // Calculate reported colleges from the proposals list (assuming each entry represents a college report)
      const reportedCollegeCodes = this.assistantcommitteequalified_proposals
        .filter(
          p =>
            p.assistantcommitteeQualifiedCancelStatus !== "cancel_proposal_file"
        )
        .map(p => p.college_code);

      this.statistics.reportedColleges = new Set(reportedCollegeCodes).size;
      this.statistics.totalColleges = this.colleges.length;
    },

    updateCharts() {
      // Update donut chart
      this.donutChartSeries = [
        this.statistics.insideCount,
        this.statistics.outsideCount
      ];

      // Update radial chart
      const totalQualified = this.statistics.totalQualified;
      if (totalQualified > 0) {
        const approvedCount = this.assistantcommitteequalifieds.filter(
          d => d.approvalStatus === "approved"
        ).length;
        const pendingCount = this.statistics.pendingApproval;
        const rejectedCount = totalQualified - approvedCount - pendingCount;

        const approvedPct = (approvedCount / totalQualified) * 100;
        const pendingPct = (pendingCount / totalQualified) * 100;
        const rejectedPct = (rejectedCount / totalQualified) * 100;

        // Use reported colleges percentage for radial total, but approval status breakdown for the bars
        // Adjusting radial chart series to reflect approval status breakdown
        this.radialChartSeries = [
          Math.round(approvedPct),
          Math.round(pendingPct),
          Math.round(rejectedPct)
        ];

        // Update the radial chart options total formatter dynamically
        this.radialChartOptions.plotOptions.radialBar.dataLabels.total.formatter = () =>
          `${this.reportPercentage}%`;
      } else {
        this.radialChartSeries = [0, 0, 0];
        this.radialChartOptions.plotOptions.radialBar.dataLabels.total.formatter = () =>
          `0%`;
      }
    },

    // --- View Methods ---
    viewDetail(item) {
      this.selectedDetail = item;
      this.detailDialog = true;
    },

    viewPdfFile(filename) {
      this.pdfFile = filename;
      this.pdfDialog = true;
    },

    viewCollegeDetail(item) {
      // Placeholder for navigating/showing detail about a college's report
      this.showSnackbar(
        "info",
        "mdi-information",
        `ดูรายละเอียดการรายงานของ ${item.college_name}`
      );
      console.log("View college:", item);
    },

    // --- Approval/Rejection Methods ---
    approveItem(item) {
      this.approvalTarget = item;
      this.approvalNote = "";
      this.approvalDialog = true;
    },

    async confirmApproval() {
      this.approvalDialog = false;
      this.detailDialog = false;

      try {
        const result = await this.$http.post(
          "assistantcommitteequalified.update.php",
          {
            ApiKey: this.ApiKey,
            assistantcommitteeQualifiedIDcard: this.approvalTarget
              .assistantcommitteeQualifiedIDcard,
            approvalStatus: "approved",
            approvalNote: this.approvalNote,
            approvalDate: new Date()
              .toISOString()
              .slice(0, 19)
              .replace("T", " ") // Standardize date format for backend
          }
        );

        if (result.data.status === true) {
          this.showSnackbar(
            "success",
            "mdi-check-circle",
            "อนุมัติเรียบร้อยแล้ว"
          );
          await this.initializeData(); // Re-initialize all data and stats
        } else {
          throw new Error(result.data.message || "การอนุมัติล้มเหลว");
        }
      } catch (error) {
        console.error("Approval error:", error);
        this.showSnackbar("error", "mdi-alert", "เกิดข้อผิดพลาดในการอนุมัติ");
      }
    },

    rejectItem(item) {
      this.rejectTarget = item;
      this.rejectReason = "";
      this.rejectDialog = true;
    },

    async confirmReject() {
      if (!this.rejectReason) return;

      this.rejectDialog = false;
      this.detailDialog = false; // Close detail dialog if open

      try {
        const result = await this.$http.post(
          "assistantcommitteequalified.update.php",
          {
            ApiKey: this.ApiKey,
            assistantcommitteeQualifiedIDcard: this.rejectTarget
              .assistantcommitteeQualifiedIDcard,
            approvalStatus: "rejected",
            rejectReason: this.rejectReason,
            approvalDate: new Date()
              .toISOString()
              .slice(0, 19)
              .replace("T", " ")
          }
        );

        if (result.data.status === true) {
          this.showSnackbar(
            "warning",
            "mdi-close-circle",
            "ไม่อนุมัติรายการเรียบร้อย"
          );
          await this.initializeData(); // Re-initialize all data and stats
        } else {
          throw new Error(result.data.message || "การไม่อนุมัติล้มเหลว");
        }
      } catch (error) {
        console.error("Rejection error:", error);
        this.showSnackbar(
          "error",
          "mdi-alert",
          "เกิดข้อผิดพลาดในการไม่อนุมัติ"
        );
      }
    },

    async bulkApprove() {
      if (this.selectedItems.length === 0) return;

      const confirmed = await this.$swal.fire({
        icon: "question",
        title: "ยืนยันการอนุมัติ",
        text: `ต้องการอนุมัติทั้งหมด ${this.selectedItems.length} รายการ?`,
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก"
      });

      if (confirmed.isConfirmed) {
        this.loading = true;
        try {
          // Prepare array of IDs for bulk update (assuming backend supports an array of ID cards)
          const idCards = this.selectedItems.map(
            item => item.assistantcommitteeQualifiedIDcard
          );

          // Assuming a new endpoint or method for bulk actions
          const result = await this.$http.post(
            "assistantcommitteequalified.bulk_update.php", // Hypothetical bulk endpoint
            {
              ApiKey: this.ApiKey,
              action: "bulk_approve",
              idCards: idCards,
              approvalStatus: "approved",
              approvalDate: new Date()
                .toISOString()
                .slice(0, 19)
                .replace("T", " ")
            }
          );

          if (result.data.status === true) {
            this.showSnackbar(
              "success",
              "mdi-check-all",
              `อนุมัติ ${this.selectedItems.length} รายการเรียบร้อย`
            );
            this.selectedItems = [];
            await this.initializeData();
          } else {
            throw new Error(
              result.data.message || "การอนุมัติชุดข้อมูลล้มเหลว"
            );
          }
        } catch (error) {
          console.error("Bulk Approval error:", error);
          this.showSnackbar(
            "error",
            "mdi-alert",
            "เกิดข้อผิดพลาดในการอนุมัติชุดข้อมูล"
          );
        } finally {
          this.loading = false;
        }
      }
    },

    async bulkReject() {
      if (this.selectedItems.length === 0) return;

      const { value: reason } = await this.$swal.fire({
        icon: "warning",
        title: "ไม่อนุมัติหลายรายการ",
        input: "textarea",
        inputLabel: "เหตุผลที่ไม่อนุมัติ",
        inputPlaceholder: "ระบุเหตุผล...",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
        inputValidator: value => {
          if (!value) {
            return "กรุณาระบุเหตุผล";
          }
        }
      });

      if (reason) {
        this.loading = true;
        try {
          const idCards = this.selectedItems.map(
            item => item.assistantcommitteeQualifiedIDcard
          );

          const result = await this.$http.post(
            "assistantcommitteequalified.bulk_update.php",
            {
              ApiKey: this.ApiKey,
              action: "bulk_reject",
              idCards: idCards,
              approvalStatus: "rejected",
              rejectReason: reason,
              approvalDate: new Date()
                .toISOString()
                .slice(0, 19)
                .replace("T", " ")
            }
          );

          if (result.data.status === true) {
            this.showSnackbar(
              "warning",
              "mdi-close-circle-multiple",
              `ไม่อนุมัติ ${this.selectedItems.length} รายการเรียบร้อย`
            );
            this.selectedItems = [];
            await this.initializeData();
          } else {
            throw new Error(
              result.data.message || "การไม่อนุมัติชุดข้อมูลล้มเหลว"
            );
          }
        } catch (error) {
          console.error("Bulk Rejection error:", error);
          this.showSnackbar(
            "error",
            "mdi-alert",
            "เกิดข้อผิดพลาดในการไม่อนุมัติชุดข้อมูล"
          );
        } finally {
          this.loading = false;
        }
      }
    },

    // --- Export Methods ---
    exportToExcel(type) {
      let data = [];
      let filename = "";
      let rawData = [];

      if (type === "filtered") {
        rawData = this.filteredData;
        filename = "ผู้ทรงคุณวุฒิ_ตามตัวกรอง";
      } else if (type === "all") {
        rawData = this.assistantcommitteequalifieds;
        filename = "ผู้ทรงคุณวุฒิทั้งหมด";
      } else if (type === "summary") {
        data = [
          { รายการ: "สถานศึกษาทั้งหมด", จำนวน: this.statistics.totalColleges },
          {
            รายการ: "สถานศึกษาที่รายงาน",
            จำนวน: this.statistics.reportedColleges
          },
          {
            รายการ: "ผู้ทรงคุณวุฒิทั้งหมด",
            จำนวน: this.statistics.totalQualified
          },
          { รายการ: "ภายใน สอศ.", จำนวน: this.statistics.insideCount },
          { รายการ: "ภายนอก/เกษียณ", จำนวน: this.statistics.outsideCount },
          { รายการ: "รออนุมัติ", จำนวน: this.statistics.pendingApproval }
        ];
        filename = "สรุปสถิติผู้ทรงคุณวุฒิ";
      }

      if (type !== "summary") {
        // Map data from rawData for Excel export
        data = rawData.map(item => ({
          ประเภท:
            item.assistantcommitteeQualifiedType === "outside"
              ? "ภายนอก/เกษียณ"
              : "ภายใน สอศ.",
          รหัสสถานศึกษา: item.assistantcommitteeQualifiedCollege,
          สถานศึกษา: item.college_name,
          รหัสบัตรประชาชน: item.assistantcommitteeQualifiedIDcard,
          คำนำหน้า: item.assistantcommitteeQualifiedTitles,
          ชื่อ: item.assistantcommitteeQualifiedFristname,
          นามสกุล: item.assistantcommitteeQualifiedLastname,
          ตำแหน่ง: item.assistantcommitteeQualifiedPosition,
          วุฒิการศึกษา_ตรี: item.assistantcommitteeQualifiedED,
          สาขา_ตรี: item.assistantcommitteeQualifiedEDSub,
          วุฒิการศึกษา_โท: item.assistantcommitteeQualifiedEDMaster,
          สาขา_โท: item.assistantcommitteeQualifiedEDMasterSub,
          วุฒิการศึกษา_เอก: item.assistantcommitteeQualifiedEDDoctoral,
          สาขา_เอก: item.assistantcommitteeQualifiedEDDoctoralSub,
          อันดับ: item.assistantcommitteeQualifiedRank || "-",
          สถานะ: this.getApprovalStatusText(item.approvalStatus)
        }));
      }

      if (data.length === 0) {
        this.showSnackbar(
          "warning",
          "mdi-alert-circle",
          "ไม่พบข้อมูลที่ต้องการส่งออก"
        );
        return;
      }

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      // Auto-width columns
      const colWidths = Object.keys(data[0] || {}).map(key => ({
        wch:
          Math.max(
            key.length,
            ...data.map(row => String(row[key] || "").length)
          ) + 2
      }));
      ws["!cols"] = colWidths;

      XLSX.writeFile(
        wb,
        `${filename}_${this.formatDateForFile(new Date())}.xlsx`
      );
      this.showSnackbar("success", "mdi-file-excel", "ส่งออกข้อมูลเรียบร้อย");
    },

    exportCollegeReport() {
      const data = this.assistantcommitteequalified_proposals.map(item => ({
        รหัสสถานศึกษา: item.college_code,
        ชื่อสถานศึกษา: item.college_name,
        // area_name is not available in the current data structure, assuming it's available in the colleges list if needed
        สถานะการรายงาน:
          item.assistantcommitteeQualifiedCancelStatus !==
          "cancel_proposal_file"
            ? "รายงานแล้ว"
            : "ยังไม่รายงาน",
        จำนวนผู้ทรงคุณวุฒิ: item.qualifiedCount,
        อัพเดตล่าสุด: item.updated_at ? this.formatDate(item.updated_at) : "-"
      }));

      if (data.length === 0) {
        this.showSnackbar(
          "warning",
          "mdi-alert-circle",
          "ไม่พบข้อมูลรายงานสถานศึกษา"
        );
        return;
      }

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "CollegeReport");

      // Auto-width columns
      const colWidths = Object.keys(data[0] || {}).map(key => ({
        wch:
          Math.max(
            key.length,
            ...data.map(row => String(row[key] || "").length)
          ) + 2
      }));
      ws["!cols"] = colWidths;

      XLSX.writeFile(
        wb,
        `รายงานสถานะสถานศึกษา_${this.formatDateForFile(new Date())}.xlsx`
      );
      this.showSnackbar("success", "mdi-file-excel", "ส่งออกรายงานเรียบร้อย");
    },

    // --- Helper Methods ---

    getRankColor(rank) {
      const colors = { "1": "amber darken-2", "2": "blue-grey" };
      return colors[rank] || "grey";
    },

    getApprovalStatusColor(status) {
      const colors = {
        approved: "success",
        rejected: "error",
        pending: "warning"
      };
      return colors[status] || "warning";
    },

    getApprovalStatusIcon(status) {
      const icons = {
        approved: "mdi-check-circle",
        rejected: "mdi-close-circle",
        pending: "mdi-clock-outline"
      };
      return icons[status] || "mdi-clock-outline";
    },

    getApprovalStatusText(status) {
      const texts = {
        approved: "อนุมัติแล้ว",
        rejected: "ไม่อนุมัติ",
        pending: "รออนุมัติ"
      };
      return texts[status] || "รออนุมัติ";
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      // Ensure date is valid for Date constructor (handle database format)
      const date = new Date(
        dateStr.replace(" ", "T") + (dateStr.endsWith("Z") ? "" : "")
      );
      if (isNaN(date.getTime())) {
        return dateStr; // Return original if parsing fails
      }
      return date.toLocaleDateString("th-TH", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },

    formatDateForFile(date) {
      return date.toISOString().split("T")[0]; // YYYY-MM-DD
    },

    showSnackbar(color, icon, text) {
      this.snackbar = {
        show: true,
        color,
        icon,
        text,
        timeout: 4000
      };
    }
  }
};
</script>

<style scoped>
/*
  ปรับปรุง Style ให้มีความเป็น Modern Dashboard มากขึ้น
  เน้นการใช้ Shadow, Border-radius และ Gradient
*/
.admin-dashboard {
  background: #f4f6f9; /* Light background for the dashboard */
  min-height: 100vh;
}

/* Stat Card Styling */
.stat-card {
  border-radius: 12px !important;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2) !important;
}

.stat-card--primary {
  background: linear-gradient(45deg, #1e88e5 0%, #1565c0 100%);
} /* Blue */
.stat-card--success {
  background: linear-gradient(45deg, #4caf50 0%, #388e3c 100%);
} /* Green */
.stat-card--warning {
  background: linear-gradient(45deg, #ffb300 0%, #f57c00 100%);
} /* Amber */
.stat-card--info {
  background: linear-gradient(45deg, #00bcd4 0%, #00acc1 100%);
} /* Cyan */

.opacity-3 {
  opacity: 0.3;
}

/* Chart Card Styling */
.chart-card {
  border-radius: 12px !important;
  height: 100%;
}

.detail-section {
  background: #f8f9fa;
  border-left: 4px solid #1976d2; /* Primary color indicator */
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 10px;
}

/* Data Table Styling */
::v-deep .v-data-table {
  border-radius: 0 0 4px 4px; /* Align with card border radius */
}

::v-deep .v-data-table thead th {
  font-weight: 700 !important;
  font-size: 14px !important;
  background: #f0f3f6 !important; /* Slightly darker header */
  color: #333 !important;
}

::v-deep .v-data-table tbody tr:hover {
  background: #e3f2fd !important; /* Light blue on hover for clarity */
}

/* Custom scroll for PDF viewer in fullscreen mode */
.fill-height {
  height: 100%;
}
</style>
